import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Avatar, Dropdown, message, Modal, Form, Input, Button } from 'antd';
import Icon, {
    HomeOutlined,
    CloudServerOutlined,
    RocketOutlined,
    HddOutlined,
    UserOutlined,
    LogoutOutlined,
    DownOutlined,
    KeyOutlined,
    TeamOutlined,
    ControlOutlined,
    ClusterOutlined,
    AppstoreOutlined,
    DashboardOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout, changePassword, editName } from '../../api/admin';
import GlobalChatAssistant from '../GlobalChatAssistant';
import GlobalCaption from '../GlobalCaption';
import './MainLayout.css';

const ProductSvg = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 1024 1024" fill="currentColor">
        <path d="m709.6 210l.4-.2h.2L512 96L313.9 209.8h-.2l.7.3L151.5 304v416L512 928l360.5-208V304zM482.7 843.6L339.6 761V621.4L210 547.8V372.9l272.7 157.3zM238.2 321.5l134.7-77.8l138.9 79.7l139.1-79.9l135.2 78l-273.9 158zM814 548.3l-128.8 73.1v139.1l-143.9 83V530.4L814 373.1z"/>
    </svg>
);
const ProductIcon = (props) => <Icon component={ProductSvg} {...props} />;

const FinanceSvg = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 1024 1024" fill="currentColor">
        <path d="M866.9 169.9L527.1 54.1C523 52.7 517.5 52 512 52s-11 .7-15.1 2.1L157.1 169.9c-8.3 2.8-15.1 12.4-15.1 21.2v482.4c0 8.8 5.7 20.4 12.6 25.9L499.3 968c3.5 2.7 8 4.1 12.6 4.1s9.2-1.4 12.6-4.1l344.7-268.6c6.9-5.4 12.6-17 12.6-25.9V191.1c.2-8.8-6.6-18.3-14.9-21.2M810 654.3L512 886.5L214 654.3V226.7l298-101.6l298 101.6zM430.5 318h-46q-2.55 0-4.8 1.2a10.1 10.1 0 0 0-4 13.6l88 161.1h-45.2c-5.5 0-10 4.5-10 10v21.3c0 5.5 4.5 10 10 10h63.1v29.7h-63.1c-5.5 0-10 4.5-10 10v21.3c0 5.5 4.5 10 10 10h63.1V658c0 5.5 4.5 10 10 10h41.3c5.5 0 10-4.5 10-10v-51.8h63.4c5.5 0 10-4.5 10-10v-21.3c0-5.5-4.5-10-10-10h-63.4v-29.7h63.4c5.5 0 10-4.5 10-10v-21.3c0-5.5-4.5-10-10-10h-45.7l87.7-161.1a10.05 10.05 0 0 0-8.8-14.8h-45c-3.8 0-7.2 2.1-8.9 5.5l-73.2 144.3l-72.9-144.3c-1.7-3.4-5.2-5.5-9-5.5"/>
    </svg>
);
const FinanceIcon = (props) => <Icon component={FinanceSvg} {...props} />;

const { Header, Sider, Content } = Layout;

// 一级菜单：ICON + 名称（窄栏），hover 弹出 二级(标题) + 三级(两列) 浮窗
// 三级菜单 key 为路由 path，点击跳转；无 children 的一级菜单直接点击跳转
const menuGroups = [
    {
        key: 'home',
        icon: <HomeOutlined />,
        label: '平台首页',
        path: '/',
    },
    {
        key: 'home-enterprise',
        icon: <DashboardOutlined />,
        label: '企业首页',
        path: '/home/dashboard',
    },
    {
        key: 'product',
        icon: <ProductIcon />,
        label: '产品',
        children: [
            {
                key: 'product-manage',
                label: '产品管理',
                children: [
                    { key: '/product/plan', label: '方案开发' },
                    { key: '/product', label: '整机开发' },
                    { key: '/product/audit', label: '产品审核' },
                    { key: '/product/network-unit', label: '配网单元' },
                    { key: '/product/ecosystem', label: '生态方案库' },
                    { key: '/product/my-solutions', label: '我的方案' },
                    { key: '/product/ecosystem-audit', label: '生态方案审核' },
                ]
            },
            {
                key: 'product-auth',
                label: '产品授权',
                children: [
                    { key: '/product/authorized-saas', label: '授权SaaS' },
                ]
            },
            {
                key: 'thing-model',
                label: '物模型',
                children: [
                    { key: '/model/iot', label: 'IOT类目' },
                    { key: '/model/device', label: '标准能力库' },
                    { key: '/model/customdevice', label: '自定义能力' },
                    { key: '/model/algorithm', label: '算法库' },
                    { key: '/model/event', label: '事件库' },
                ]
            },
            {
                key: 'device-sdk',
                label: '设备SDK',
                children: [
                    { key: '/compile/toolchain', label: '编译链管理' },
                    { key: '/compile/task', label: '编译任务' },
                    { key: '/compile/sdk-version', label: 'SDK版本管理' },
                ]
            },
        ]
    },
    {
        key: 'device',
        icon: <HddOutlined />,
        label: '设备',
        children: [
            {
                key: 'firmware',
                label: '固件管理',
                children: [
                    { key: '/firmware', label: '我的固件' },
                ]
            },
            {
                key: 'production',
                label: '生产管理',
                children: [
                    { key: '/production/productionOrder', label: '生产工单' },
                    { key: '/production/prodTestAccount', label: '产测账号' },
                    { key: '/device/license-query', label: 'License查询' },
                    { key: '/production/prodTestAfterSales', label: '产测售后' },
                    { key: '/production/prodTestStatistics', label: '产测统计' },
                    { key: '/production/uniqueid-license', label: '外部授权码' },
                    { key: '/produce/tag', label: '设备标签' },
                ]
            },
            {
                key: 'module-change',
                label: '模组改号',
                children: [
                    { key: '/production/change-number-order', label: '改号工单' },
                    { key: '/production/manual-change', label: '发起改号' },
                    { key: '/production/device-ownership', label: '变更记录' },
                ]
            },
            {
                key: 'device-center',
                label: '设备中心',
                children: [
                    { key: '/device/list', label: '设备管理' },
                    { key: '/device/transfer', label: '设备调拨' },
                    { key: '/device/firmware/ota', label: '固件OTA' },
                    { key: '/device/maintenance', label: '设备运维' },
                ]
            },
        ]
    },
    {
        key: 'app-service',
        icon: <CloudServerOutlined />,
        label: '应用',
        children: [
            {
                key: 'app-manage',
                label: 'App',
                children: [
                    { key: '/app/manage', label: 'App管理' },
                    { key: '/app/audit', label: '待审核App' },
                    { key: '/app/audit/manage', label: '全局App管理' },
                    { key: '/app/merchant-platform', label: '平台商户号' },
                    { key: '/app/merchant-enterprise', label: '企业商户号' },
                ]
            },
            {
                key: 'private-deploy',
                label: '私有化部署',
                children: [
                    { key: '/app/private-deploy', label: '我的私有部署' },
                    { key: '/app/private-deploy-audit', label: '私有部署管理' },
                ]
            },
            {
                key: 'oem-custom',
                label: 'OEM App 定制',
                children: [
                    { key: '/app/oem-custom', label: '我的定制' },
                    { key: '/app/oem-custom-audit', label: '定制管理' },
                ]
            },
            {
                key: 'saas-app',
                label: 'SaaS应用',
                children: [
                    { key: '/app/self-saas', label: '我的SaaS' },
                    { key: '/app/saas-audit', label: 'SaaS审核' },
                ]
            },
        ]
    },
    {
        key: 'operation',
        icon: <ControlOutlined />,
        label: '运营',
        children: [
            {
                key: 'agent-factory',
                label: 'Agent工厂',
                children: [
                    { key: '/agent/models', label: '模型管理' },
                    { key: '/agent/events', label: '触发事件管理' },
                    { key: '/agent/list', label: 'Agent管理' },
                ]
            },
            {
                key: 'flow-card',
                label: '流量卡',
                children: [
                    { key: '/4g/cardDefinition', label: '卡商定义' },
                    { key: '/4g/cardDealerConfig', label: '卡商管理' },
                    { key: '/4g/cardConfig', label: '4G卡管理' },
                ]
            },
            {
                key: 'package-manage',
                label: '套餐管理',
                children: [
                    { key: '/package/service', label: '服务类型' },
                    { key: '/package/template', label: '套餐服务管理' },
                    { key: '/package/app-package', label: '套餐配置' },
                    { key: '/package/gift', label: '赠送套餐' },
                    { key: '/package/admin', label: '超管开关套餐' },
                    { key: '/package/transfer', label: '套餐转移' },
                ]
            },
            {
                key: 'package-sale',
                label: '套餐售卖',
                children: [
                    { key: '/operation/mall', label: '商城位运营' },
                    { key: '/operation/recommend', label: '推荐位运营' },
                    { key: '/operation/custom-sale', label: '自定义售卖' },
                ]
            },
            {
                key: 'trial-convert',
                label: '增值转化运营',
                children: [
                    { key: '/operation/trial-convert', label: '试用转化策略' },
                ]
            },
            {
                key: 'user-portrait',
                label: '用户画像',
                children: [
                    { key: '/operation/user-segment', label: '用户分群' },
                ]
            },
            {
                key: 'coupon',
                label: '卡券管理',
                children: [
                    { key: '/operation/coupon', label: '卡券列表' },
                    { key: '/operation/coupon/issue', label: '发放记录' },
                    { key: '/operation/coupon/records', label: '核销记录' },
                ]
            },
            {
                key: 'ad-delivery',
                label: '广告投放',
                children: [
                    { key: '/operation/ad-slot', label: '广告配置管理' },
                    { key: '/operation/ad-strategy', label: '广告投放策略' },
                    { key: '/operation/ad-platform', label: '广告平台配置' },
                    { key: '/ad/whitelist', label: '精准广告策略' },
                ]
            },
        ]
    },
    {
        key: 'traffic-pool',
        icon: <ClusterOutlined />,
        label: '流量池',
        children: [
            {
                key: 'traffic-pool-manage',
                label: '流量池配置',
                children: [
                    { key: '/traffic/pool', label: '流量池配置' },
                    { key: '/traffic/card', label: '流量卡管理' },
                    { key: '/traffic/package', label: '流量套餐' },
                    { key: '/traffic/order', label: '流量订单' },
                ]
            },
        ]
    },
    {
        key: 'customer',
        icon: <TeamOutlined />,
        label: '客户',
        children: [
            {
                key: 'my-customer',
                label: '我的客户',
                children: [
                    { key: '/company/sub', label: '平台客户' },
                    { key: '/company/cooperation', label: '合作企业' },
                    { key: '/company/list', label: '下级企业' },
                    { key: '/company/brand', label: '品牌设置' },
                    { key: '/company/info', label: '企业信息' },
                ]
            },
            {
                key: 'customer-service',
                label: '客户服务',
                children: [
                    { key: '/customer/crm', label: '客户跟踪' },
                    { key: '/customer/crm/tags', label: '客户标签' },
                    { key: '/customer/ticket-manage', label: '工单管理' },
                    { key: '/customer/agent-manage', label: '客服账号' },
                ]
            },
            {
                key: 'device-distribution',
                label: '设备分销',
                children: [
                    { key: '/distribution/my', label: '我的设备分销' },
                    { key: '/distribution/proxy', label: '代理设备分销' },
                ]
            },
            {
                key: 'order-profit',
                label: '设备订单分润',
                children: [
                    { key: '/share/business-config', label: '底价手续费' },
                    { key: '/share/profit/app', label: 'App分润' },
                    { key: '/share/profit/again', label: '分润再分配' },
                    { key: '/share/profit/expend', label: '分润支出' },
                    { key: '/share/profit/income', label: '分润收益' },
                ]
            },
            {
                key: 'ad-profit',
                label: '设备广告分润',
                children: [
                    { key: '/share/ad-profit/config', label: '分润配置' },
                    { key: '/share/ad-profit/expend', label: '分润支出' },
                    { key: '/share/ad-profit/income', label: '分润收益' },
                ]
            },
        ]
    },
    {
        key: 'data',
        icon: <BarChartOutlined />,
        label: '数据',
        children: [
            {
                key: 'ent-data',
                label: '经营看板',
                children: [
                    { key: '/ent-data/dashboard', label: '方案应用情况' },
                    { key: '/ent-data/device-dashboard', label: '设备盈亏分析' },
                    { key: '/ent-data/order-dashboard', label: 'App云存收益' },
                    { key: '/ent-data/ad-revenue', label: 'App广告收益' },
                    { key: '/ent-data/fourg-revenue', label: '4G流量收益' },
                    { key: '/ent-data/device-brand', label: '设备品牌统计' },
                ]
            },
        ]
    },
    {
        key: 'finance',
        icon: <FinanceIcon />,
        label: '账务',
        children: [
            {
                key: 'purchase-order',
                label: '采购订单',
                children: [
                    { key: '/order/production/auth', label: '生产授权采购' },
                    { key: '/order/company/production', label: '企业生产采购' },
                ]
            },
            {
                key: 'value-order',
                label: '增值订单',
                children: [
                    { key: '/order/services', label: '增值服务订单' },
                    { key: '/order/4g', label: '4G流量订单' },
                ]
            },
            {
                key: 'public-cloud-resource',
                label: '公有云资源',
                children: [
                    { key: '/cost/traffic-bill', label: '设备流量账单' },
                    { key: '/cost/traffic-stat', label: '设备流量统计' },
                ]
            },
            {
                key: 'cost-center',
                label: '费用中心',
                children: [
                    { key: '/cost/consumption/account', label: '消费账户' },
                    { key: '/cost/income', label: '收益账户' },
                    { key: '/cost/invoices', label: '发票管理' },
                ]
            },
            {
                key: 'platform-finance',
                label: '平台账务',
                children: [
                    { key: '/finance/rechargAudit', label: '充值审核' },
                    { key: '/finance/direct-topup', label: '生产授权直充' },
                    { key: '/finance/creditLimit', label: '信用额度' },
                    { key: '/finance/exchangeRate', label: '汇率管理' },
                    { key: '/finance/invoice', label: '发票管理' },
                    { key: '/finance/withdrawal/audit', label: '提现审核' },
                    { key: '/finance/commission/order', label: '抽佣账单' },
                    { key: '/finance/ad-commission/order', label: '广告抽佣账单' },
                    { key: '/finance/private-deploy-bill', label: '私有部署账单' },
                ]
            },
            {
                key: 'biz-config',
                label: '商务配置',
                children: [
                    { key: '/finance/traffic-group', label: '设备流量商务' },
                ]
            },
        ]
    },
    {
        key: 'other',
        icon: <AppstoreOutlined />,
        label: '其他',
        children: [
            {
                key: 'customer-tool',
                label: '客服工具',
                children: [
                    { key: '/customer/order-query', label: '增值订单查询' },
                    { key: '/customer/ticket/my', label: '我的工单' },
                    { key: '/customer/service-tracking', label: '服务跟踪' },
                    { key: '/customer/service-system', label: '客服系统' },
                ]
            },
            {
                key: 'global-dispatch',
                label: '全球调度',
                children: [
                    { key: '/dispatch/serviceRegion', label: '服务区域' },
                    { key: '/dispatch/product', label: '产品调度' },
                    { key: '/dispatch/device', label: '设备调度' },
                ]
            },
            {
                key: 'audit-log',
                label: '日志审计',
                children: [
                    { key: '/audit-log/platform', label: '平台日志' },
                    { key: '/audit-log', label: '操作日志' },
                ]
            },
            {
                key: 'super-admin',
                label: '系统设置',
                children: [
                    { key: '/root/menu', label: '菜单配置' },
                    { key: '/companyRole', label: '企业角色' },
                    { key: '/companyAdmin', label: '企业账号管理' },
                    { key: '/release-notes', label: '发版公告' },
                ]
            },
            {
                key: 'permission-manage',
                label: '权限管理',
                children: [
                    { key: '/insideRole', label: '内部角色' },
                    { key: '/insideAccount', label: '内部账号' },
                ]
            },
        ]
    },
];

const MainLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [userInfo, setUserInfo] = useState(null);
    const [changePwdVisible, setChangePwdVisible] = useState(false);
    const [pwdForm] = Form.useForm();
    const [changeNameVisible, setChangeNameVisible] = useState(false);
    const [nameForm] = Form.useForm();
    const [unreadBadgeCount, setUnreadBadgeCount] = useState(0);
    const [activeMenu, setActiveMenu] = useState(null); // 当前 hover 高亮的一级菜单 key
    const [popoverMenu, setPopoverMenu] = useState(null); // 浮窗内容锁定的菜单组 key（置顶展示，鼠标移动途中不切换）

    // 构建 路由path → 一级菜单key 的映射，用于自动选中当前页面所属一级菜单
    // 菜单不做角色过滤：经营统计三个菜单始终展示，权限控制由页面级 403 拦截 + 数据范围 + Tab 隐藏实现
    const pathToGroupMap = useMemo(() => {
        const map = {};
        menuGroups.forEach((group) => {
            if (group.path) map[group.path] = group.key;
            (group.children || []).forEach((sub) => {
                (sub.children || []).forEach((item) => {
                    map[item.key] = group.key;
                });
            });
        });
        return map;
    }, []);

    const selectedKey = useMemo(() => {
        const pathname = location.pathname;
        if (pathToGroupMap[pathname]) return pathToGroupMap[pathname];
        // 兜底：详情页等非菜单路由，按最长前缀匹配所属一级菜单
        let bestKey = null;
        let bestLen = 0;
        Object.keys(pathToGroupMap).forEach((p) => {
            if (pathname === p || pathname.startsWith(p + '/')) {
                if (p.length > bestLen) {
                    bestLen = p.length;
                    bestKey = pathToGroupMap[p];
                }
            }
        });
        return bestKey;
    }, [location.pathname, pathToGroupMap]);

    // 浮窗置顶展示：浮窗撑满侧栏高度，鼠标水平移入不经过其他菜单项
    const popoverGroup = menuGroups.find((g) => g.key === popoverMenu) || null;

    const handleGroupEnter = (key) => {
        setActiveMenu(key);
        setPopoverMenu(key);
    };
    const handleNavLeave = () => {
        setActiveMenu(null);
        setPopoverMenu(null);
    };
    const handleMenuClick = (key) => {
        setActiveMenu(null);
        setPopoverMenu(null);
        navigate(key);
    };

    useEffect(() => {
        const infoStr = localStorage.getItem('user_info');
        if (infoStr) {
            setUserInfo(JSON.parse(infoStr));
        }
    }, []);

    // 轮询未读公告数量（每30秒检查一次，标记已读后通过事件即时更新）
    useEffect(() => {
        // Mock: 各角色可见的公告总数（与 Announcement.js 中 MOCK_ALL 保持一致）
        const MOCK_VISIBLE_TOTAL = { enterprise: 6, platform: 5 };
        const updateBadge = () => {
            try {
                const readMap = JSON.parse(localStorage.getItem('announcement_read') || '{}');
                const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
                const role = userInfo?.role || 'enterprise';
                const accountId = userInfo?.user_name || 'mock_user';
                const totalVisible = MOCK_VISIBLE_TOTAL[role] || 6;
                // 账号级别的已读计数：key 格式为 {accountId}_{role}_{announcementKey}
                const readCount = Object.keys(readMap).filter(k => k.startsWith(`${accountId}_${role}_`)).length;
                setUnreadBadgeCount(Math.max(0, totalVisible - readCount));
            } catch { setUnreadBadgeCount(0); }
        };
        updateBadge();
        const interval = setInterval(updateBadge, 30000);
        window.addEventListener('badge_update', updateBadge);
        return () => {
            clearInterval(interval);
            window.removeEventListener('badge_update', updateBadge);
        };
    }, []);

    const handleLogout = async () => {
        try {
            const token = localStorage.getItem('user_token');
            const res = await logout({ user_token: token });
            if (res.code === 0) {
                // 成功处理
            } else {
                if (res.message) message.error(res.message);
            }
        } catch (error) {
            console.error('Logout error:', error);
            if (error.message) message.error(error.message);
        } finally {
            localStorage.removeItem('user_token');
            localStorage.removeItem('user_info');
            navigate('/login');
        }
    };

    const handleChangePwdClick = () => {
        setChangePwdVisible(true);
        pwdForm.resetFields();
    };

    const handleChangePwdSubmit = async () => {
        try {
            const values = await pwdForm.validateFields();
            const res = await changePassword({
                old_pass: values.old_pass,
                new_pass: values.new_pass,
                confirm_pass: values.confirm_pass
            });

            if (res.code === 0) {
                message.success('修改成功, 请重新登录');
                setChangePwdVisible(false);
                localStorage.removeItem('user_token');
                localStorage.removeItem('user_info');
                navigate('/login');
            } else {
                message.error(res.message || '修改失败');
            }
        } catch (error) {
            console.error('Change password failed:', error);
            if (error.errorFields) return;
            message.error(error.message || '修改失败');
        }
    };

    const handleChangeNameSubmit = async () => {
        try {
            const values = await nameForm.validateFields();
            const res = await editName({
                nick_name: values.nick_name
            });

            if (res.code === 0) {
                message.success('修改成功');
                setChangeNameVisible(false);
                const newUserInfo = { ...userInfo, nick_name: values.nick_name };
                setUserInfo(newUserInfo);
                localStorage.setItem('user_info', JSON.stringify(newUserInfo));
            } else {
                message.error(res.message || '修改失败');
            }
        } catch (error) {
            console.error('Change name failed:', error);
            if (error.errorFields) return;
            message.error(error.message || '修改失败');
        }
    };

    const userMenuItems = [
        {
            key: 'personalCenter',
            icon: <UserOutlined />,
            label: '个人信息',
            onClick: () => navigate('/personal-center'),
        },
        {
            key: 'changePwd',
            icon: <KeyOutlined />,
            label: '修改密码',
            onClick: handleChangePwdClick,
        },
        {
            key: 'divider',
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: '退出登录',
            onClick: handleLogout,
        },
    ];

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider width={90} className="site-layout-sider">
                <div className="logo-container">
                    <div className="logo-icon">HM</div>
                </div>
                <nav className="side-nav" onMouseLeave={handleNavLeave}>
                    {menuGroups.map((group) => (
                        <div
                            key={group.key}
                            className={`side-nav-item${selectedKey === group.key ? ' active' : ''}${activeMenu === group.key ? ' hover' : ''}`}
                            onMouseEnter={() => handleGroupEnter(group.key)}
                            onClick={() => group.path && handleMenuClick(group.path)}
                        >
                            <span className="side-nav-icon">{group.icon}</span>
                            <span className="side-nav-label">{group.label}</span>
                        </div>
                    ))}

                    {/* hover 浮窗：置顶展示，内容锁定首次进入的菜单组 */}
                    {popoverGroup && popoverGroup.children && (
                        <div
                            className="side-nav-popover"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="side-nav-pop-grid">
                                {popoverGroup.children.map((sub) => (
                                    <div key={sub.key} className="side-nav-pop-col">
                                        <div className="side-nav-pop-sub">{sub.label}</div>
                                        <div className="side-nav-pop-items">
                                            {sub.children.map((item) => (
                                                <div
                                                    key={item.key}
                                                    className={`side-nav-pop-item${location.pathname === item.key ? ' active' : ''}`}
                                                    onClick={() => handleMenuClick(item.key)}
                                                >
                                                    {item.label}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </nav>
            </Sider>
            <Layout className="site-layout">
                <Header className="site-header">
                    {/* Header Left Content */}
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 16 }}>
                        {/* <span style={{ color: '#ff002b', marginRight: 8, fontSize: '1.5em', fontWeight: 500 }}>需求文档：</span>
                        <Select
                            showSearch
                            placeholder="请选择需求文档"
                            style={{ width: 220 }}
                            value={docSelectValue}
                            optionFilterProp="children"
                            onSelect={(value) => {
                                if (value) {
                                  window.open(value, '_blank');
                                  setDocSelectValue(null);
                                }
                            }}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={[
                                // { label: '待评审-企业首页看板', value: 'https://my.feishu.cn/docx/I3lwdqf7loK5OPxHGKtcCcyin8K'},
                                { label: '待评审-编译设备SDK', value: 'https://my.feishu.cn/docx/MahudDWCsoohAvxZcgzcFa39nwc'},
                                { label: '开发中-0419配网单元', value: 'https://my.feishu.cn/docx/FfkUdioybo3ou5xFT2Qc7HIvnle'},
                                { label: '0419固件管理优化', value: 'https://my.feishu.cn/docx/XjCIdbhFOouicSxOqy0cRCFqn5d'},
                                { label: '0419生产优化', value: 'https://my.feishu.cn/docx/RsR9df9dKogcwZxhpAgcrNsbnYd'},
                                { label: '0413收益提现', value: 'https://my.feishu.cn/docx/LUYIdgrDKoHzzXx0y4rcj5yUnwc'},
                                { label: '0413设备分销和分润管理', value: 'https://my.feishu.cn/docx/BToMd2lgCocFu6xV7Pxc4tuNnCd'},
                                { label: '0408平台抽佣', value: 'https://my.feishu.cn/docx/AmUjdf10oofb7xxE57sc5JysnlX'},
                                { label: '0407产品限制使用区域', value: 'https://my.feishu.cn/docx/KOhHdXyA4oipQvxnNgecoUoTnff'},
                                { label: '0403App下载页管理', value: 'https://my.feishu.cn/docx/DJf1dYdO5odSgpxdCLacyCLAn6d'},
                                { label: '0403设备调度', value: 'https://my.feishu.cn/docx/UgNHdUGttoYeU2xPM7ucDapVnLh'},
                                { label: '0326增值订单优化', value: 'https://my.feishu.cn/docx/Eqm6d2xniox8AvxQbrvcJj5anPc'},
                                { label: '0326套餐模块优化', value: 'https://my.feishu.cn/docx/LekqdEjqHomDeWxaQ8HcptWDneg'},
                                { label: '0324赠送授权', value: 'https://my.feishu.cn/docx/MoE6dNQ0KoF9YYxEB5KcT4ocnOc'},
                                { label: '0323账务模块', value: 'https://my.feishu.cn/docx/IaLDdzxU1oANeLxoh8HcLkLWnwc'},
                                { label: '0321企业管理', value: 'http://rp.wzxn.top/xzc/v2_BusinessManagement/index.html'},
                                { label: '0320菜单&角色&用户', value: 'https://my.feishu.cn/docx/C3R3dxmVVoT49AxeRCmcgNQQnAf'},
                                { label: '0317卡商(4G)管理', value: 'https://my.feishu.cn/docx/Cxf0d3XaJovgjbx2RJQcrjZ1n4e'},
                                { label: '0316套餐服务管理', value: 'https://my.feishu.cn/docx/XHt9dh8WtoyH9IxiPKqcggTPnLg'},
                                { label: '0313应用管理', value: 'https://my.feishu.cn/docx/ZZTfdb4aDoicIcxa42Xcu3odnfb'},
                                { label: '0311固件&生产', value: 'https://my.feishu.cn/docx/Je3SdKhE6o6YhLxCO2Octwf3nL6'},
                                { label: '0310产品开发管理', value: 'https://my.feishu.cn/docx/RNqBdc5HyoqDXmxnD1gcxLa8nhd'},
                                { label: '0306产品App配置', value: 'https://my.feishu.cn/docx/NDv6dPEWToVkFWx3allcdOkWnAc'},
                                { label: '0302订单管理', value: 'https://my.feishu.cn/docx/GABjdLTCbok80kxJYU1c80kHnsg'},
                            ]}
                        /> */}
                    </div>

                    {/* Header Right Content - User Profile */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* 发版公告入口（平台用户和企业用户均可见） */}
                        <Button
                            type="text"
                            icon={<RocketOutlined />}
                            onClick={() => navigate('/announcement')}
                            style={{ marginRight: 8, fontSize: 13 }}
                        >
                            更新日志
                            {unreadBadgeCount > 0 && (
                                <span style={{
                                    display: 'inline-block',
                                    background: '#ff4d4f',
                                    color: '#fff',
                                    borderRadius: 10,
                                    minWidth: 20,
                                    height: 20,
                                    lineHeight: '20px',
                                    textAlign: 'center',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    marginLeft: 6,
                                    padding: '0 6px',
                                }}>
                                    {unreadBadgeCount > 99 ? '99+' : unreadBadgeCount}
                                </span>
                            )}
                        </Button>
                        <div style={{ cursor: 'pointer' }}>
                            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar style={{ backgroundColor: '#ff6a00', marginRight: 8 }} icon={<UserOutlined />} />
                                    <span style={{ color: '#333', marginRight: 8 }}>{userInfo?.nick_name || userInfo?.user_name || 'User'}</span>
                                    <DownOutlined style={{ fontSize: '12px', color: '#999' }} />
                                </div>
                            </Dropdown>
                        </div>
                    </div>
                </Header>
                <Content style={{ margin: '24px 0 0', overflow: 'initial' }}>
                    <div className="site-content">
                        {children}
                    </div>
                </Content>
            </Layout>

            {/* 修改密码 Modal */}
            <Modal
                title="修改密码"
                open={changePwdVisible}
                onOk={handleChangePwdSubmit}
                onCancel={() => setChangePwdVisible(false)}
                okText="确认"
                cancelText="取消"
                centered
            >
                <Form form={pwdForm} layout="vertical">
                    <Form.Item
                        name="old_pass"
                        label="旧密码"
                        rules={[{ required: true, message: '请输入旧密码' }]}
                    >
                        <Input.Password placeholder="请输入旧密码" />
                    </Form.Item>
                    <Form.Item
                        name="new_pass"
                        label="新密码"
                        rules={[{ required: true, message: '请输入新密码' }]}
                    >
                        <Input.Password placeholder="请输入新密码" />
                    </Form.Item>
                    <Form.Item
                        name="confirm_pass"
                        label="确认密码"
                        dependencies={['new_pass']}
                        rules={[
                            { required: true, message: '请确认新密码' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('new_pass') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('两次输入的密码不一致'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="请再次输入新密码" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 修改昵称 Modal */}
            <Modal
                title="修改昵称"
                open={changeNameVisible}
                onOk={handleChangeNameSubmit}
                onCancel={() => setChangeNameVisible(false)}
                okText="保存"
                cancelText="取消"
                centered
            >
                <Form form={nameForm} layout="vertical">
                    <Form.Item
                        name="nick_name"
                        label="新昵称"
                        rules={[{ required: true, message: '请输入新昵称' }]}
                    >
                        <Input placeholder="请输入新昵称" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 全局浮窗字幕（流光边框通知） */}
            <GlobalCaption />

            {/* AI 助手 — 全局悬浮入口 + 对话抽屉 */}
            <GlobalChatAssistant />
        </Layout>
    );
};

export default MainLayout;
